// Scroll-driven journeys - data layer.
//
// The scroll mechanic in DescentEngine knows nothing about the ocean. It
// consumes a DescentConfig, so a dive, a climb into space and a walk back
// through deep time are all just configs in this file.

export type DescentNode = {
  /** Position on the journey's scale (metres, kilometres, years ago…). */
  depthMeters: number;
  /** A landmark ("marker") or a linked discovery ("discovery"). */
  type: "marker" | "discovery";
  /** For discovery nodes: slug of the article. */
  slug?: string;
  /** Marker headline. Discovery nodes use the article title instead. */
  label?: string;
  /** One supporting line. Discovery nodes fall back to the excerpt. */
  subtext?: string;
};

export type ColorStop = { depth: number; rgb: [number, number, number] };

export type DescentConfig = {
  /** URL key under /journeys/$key. Ocean also lives at /descent. */
  key: string;
  title: string;
  /** One line under the title on the opening card. */
  intro: string;
  /** Homepage card copy. */
  card: { eyebrow: string; line: string; sub: string };
  /** Collections whose published discoveries hydrate the nodes. */
  collectionSlugs: string[];
  /** Where "all discoveries" at the end points. */
  collectionSlug: string;
  maxDepth: number;
  unit: string;
  /** Scroll easing: >1 gives the crowded start more scroll distance. */
  curve: number;
  /** Optional pretty formatter for the big HUD number. */
  format?: (v: number) => string;
  /** Optional secondary readout (e.g. pressure). */
  secondary?: (v: number) => string;
  zones: Array<{ under: number; label: string }>;
  statuses: Array<{ under: number; text: string }>;
  colorStops: ColorStop[];
  /** Neon accent (hex) driving glows, rails and highlights on this journey. */
  accent: string;
  /** "up" journeys (altitude) read as rising: rails and gauges fill upwards. */
  direction?: "down" | "up";
  /** Closing line under the final number. */
  outro: string;
  /** Share sentence; {v} is replaced with the formatted max reached. */
  shareLine: string;
  nodes: DescentNode[];
};

/** Pressure in atmospheres: 1 at the surface, +1 per 10 m of seawater. */
export function pressureAt(depthMeters: number): number {
  return 1 + depthMeters / 10;
}

export function depthForProgress(progress: number, maxDepth: number, curve = 2.6): number {
  const p = Math.max(0, Math.min(1, progress));
  return Math.pow(p, curve) * maxDepth;
}

export function progressForDepth(depth: number, maxDepth: number, curve = 2.6): number {
  const d = Math.max(0, Math.min(depth, maxDepth));
  return Math.pow(d / maxDepth, 1 / curve);
}

/** Depth-to-colour, interpolated continuously so the fade never steps. */
export function colorAt(value: number, stops: ColorStop[]): string {
  const last = stops[stops.length - 1];
  const d = Math.max(0, Math.min(value, last.depth));
  for (let i = 1; i < stops.length; i++) {
    const a = stops[i - 1];
    const b = stops[i];
    if (d <= b.depth) {
      const t = (d - a.depth) / (b.depth - a.depth || 1);
      const mix = a.rgb.map((v, k) => Math.round(v + (b.rgb[k] - v) * t));
      return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
    }
  }
  return `rgb(${last.rgb.join(", ")})`;
}

export function pickBand<T extends { under: number }>(value: number, bands: T[]): T {
  for (const b of bands) if (value < b.under) return b;
  return bands[bands.length - 1];
}

export function zoneAt(value: number, config: DescentConfig): string {
  return pickBand(value, config.zones).label;
}

export function bodyStatusAt(value: number, config: DescentConfig): string {
  return pickBand(value, config.statuses).text;
}

/** 4,600,000,000 is unreadable. "4.6 billion" is not. */
export function bigYears(v: number): string {
  if (v >= 1e9) return `${(v / 1e9).toFixed(v >= 1e10 ? 0 : 1)} billion`;
  if (v >= 1e6) return `${Math.round(v / 1e6).toLocaleString()} million`;
  if (v >= 1e4) return `${Math.round(v / 1e3).toLocaleString()},000`;
  return Math.round(v).toLocaleString();
}

/**
 * Depth as a piecewise-linear function of scroll progress, anchored to where
 * the nodes actually sit on the track. This is what keeps the HUD honest:
 * whatever card is at the centre of the screen is the number on the gauge.
 */
export function depthForAnchoredProgress(
  progress: number,
  anchors: Array<{ p: number; d: number }>,
): number {
  const p = Math.max(0, Math.min(1, progress));
  if (anchors.length === 0) return 0;
  if (p <= anchors[0].p) return anchors[0].d;
  for (let i = 1; i < anchors.length; i++) {
    const a = anchors[i - 1];
    const b = anchors[i];
    if (p <= b.p) {
      const t = (p - a.p) / (b.p - a.p || 1);
      return a.d + (b.d - a.d) * t;
    }
  }
  return anchors[anchors.length - 1].d;
}

/**
 * Depths are habitat/landmark placements, not claims made by the linked
 * article. Markers use verifiable figures; discovery nodes sit where the
 * subject actually occurs, so the journey stays honest.
 */
export const OCEAN_DESCENT: DescentConfig = {
  key: "ocean",
  title: "The Descent",
  intro:
    "Scroll from the surface to the deepest point on Earth. 10,935 metres straight down. It gets darker the further you go, and it does not get friendlier.",
  card: {
    eyebrow: "The Descent · Interactive",
    line: "Scroll from 0 m to 10,935 m. See how deep you survive.",
    sub: "The light fades, the pressure climbs, and something lives at every depth.",
  },
  collectionSlugs: ["ocean-deep"],
  collectionSlug: "ocean-deep",
  maxDepth: 10935,
  unit: "m",
  curve: 2.6,
  secondary: (v) => `${Math.round(pressureAt(v)).toLocaleString()} atm`,
  zones: [
    { under: 200, label: "Sunlight zone" },
    { under: 1000, label: "Twilight zone" },
    { under: 4000, label: "Midnight zone" },
    { under: 6000, label: "Abyssal zone" },
    { under: Infinity, label: "Hadal zone" },
  ],
  statuses: [
    { under: 10, text: "You are fine. Enjoy it while it lasts." },
    { under: 40, text: "A held breath has compressed your lungs to half their size." },
    { under: 100, text: "Past a free diver's reach. Your lungs are the size of a grapefruit." },
    { under: 300, text: "Nitrogen turns narcotic here. Divers forget which way the surface is." },
    { under: 1000, text: "Unprotected, your ribcage has already collapsed." },
    { under: 3000, text: "Roughly 40 cars of weight pressing on every square centimetre of you." },
    { under: 6000, text: "Steel hulls fail around here. Water at 2°C, and no sunlight has ever reached it." },
    { under: 9000, text: "Only a handful of machines ever built have survived this depth." },
    { under: Infinity, text: "Over 1,000 atmospheres. A submarine implodes in milliseconds." },
  ],
  accent: "#22d3ee",
  colorStops: [
    { depth: 0, rgb: [10, 32, 44] },
    { depth: 150, rgb: [8, 26, 38] },
    { depth: 400, rgb: [6, 20, 32] },
    { depth: 1000, rgb: [5, 15, 26] },
    { depth: 2500, rgb: [4, 10, 18] },
    { depth: 6000, rgb: [2, 6, 11] },
    { depth: 10935, rgb: [1, 2, 5] },
  ],
  outro:
    "Around a quarter of the ocean floor has been mapped in detail. Everything else down there is still unopened.",
  shareLine: "I got {v} m down before the ocean stopped me. See how deep you survive:",
  nodes: [
    { depthMeters: 0, type: "marker", label: "Sea level", subtext: "One atmosphere. Sunlight everywhere. Keep scrolling." },
    { depthMeters: 3, type: "discovery", slug: "unnatural-whale-death-mystery" },
    { depthMeters: 5, type: "discovery", slug: "lemon-sharks-social-predators-mangroves" },
    { depthMeters: 10, type: "discovery", slug: "the-box-jellyfish-kills-60-people-a-year" },
    { depthMeters: 20, type: "discovery", slug: "jellyfish-that-might-escape-aging" },
    { depthMeters: 25, type: "discovery", slug: "pufferfish-underwater-sand-art" },
    { depthMeters: 32, type: "discovery", slug: "sea-lions-just-curious-about-humans" },
    { depthMeters: 40, type: "marker", label: "Recreational scuba limit", subtext: "Past this, ordinary air becomes dangerous to breathe." },
    { depthMeters: 48, type: "discovery", slug: "sharks-dont-fear-humans-because-they-cant-see-us" },
    { depthMeters: 65, type: "discovery", slug: "ghost-fishing-nets-eternal-traps" },
    { depthMeters: 110, type: "discovery", slug: "blue-whale-circulatory-system-size" },
    { depthMeters: 200, type: "marker", label: "The twilight zone", subtext: "Sunlight is now too weak for photosynthesis. Plants stop here." },
    { depthMeters: 240, type: "discovery", slug: "ocean-gold-dissolved-treasure" },
    { depthMeters: 330, type: "discovery", slug: "octopuses-have-three-hearts-and-blue-blood" },
    { depthMeters: 500, type: "discovery", slug: "meet-the-largest-marine-arthropods" },
    { depthMeters: 700, type: "discovery", slug: "how-sea-bioluminescence-works" },
    { depthMeters: 820, type: "discovery", slug: "polar-gigantism-sea-spiders" },
    { depthMeters: 1000, type: "marker", label: "The midnight zone", subtext: "The last of the sunlight is gone. 101 atmospheres. Everything below this is permanently dark." },
    { depthMeters: 1100, type: "discovery", slug: "colossal-squid-giant-eyes" },
    { depthMeters: 1300, type: "discovery", slug: "goblin-shark-jaw-ejection" },
    { depthMeters: 2000, type: "marker", label: "Sperm whale hunting grounds", subtext: "An air-breathing mammal is still down here, holding its breath." },
    { depthMeters: 2500, type: "discovery", slug: "deep-sea-vents-extreme-life" },
    { depthMeters: 3800, type: "marker", label: "The Titanic", subtext: "Resting on the abyssal plain in near-freezing black water." },
    { depthMeters: 4200, type: "discovery", slug: "anomaly-where-gravity-misbehaves" },
    { depthMeters: 5000, type: "discovery", slug: "submarine-crush-depth-survival" },
    { depthMeters: 6000, type: "marker", label: "The hadal zone", subtext: "Named after Hades. Only ocean trenches go deeper than this." },
    { depthMeters: 8000, type: "discovery", slug: "walk-to-the-deepest-ocean" },
    { depthMeters: 10000, type: "discovery", slug: "basketball-at-ocean-bottom" },
    { depthMeters: 10935, type: "marker", label: "Challenger Deep", subtext: "The bottom. Fewer people have been here than have walked on the Moon." },
  ],
};

/** The Ascent - ground level to the Moon, in kilometres of altitude. */
export const SPACE_ASCENT: DescentConfig = {
  key: "space",
  title: "The Ascent",
  intro:
    "Scroll straight up. Ground level to the Moon, 384,400 kilometres of it. The air runs out early and almost nothing up here wants you alive.",
  card: {
    eyebrow: "The Ascent · Interactive",
    line: "Scroll from the ground to the Moon. 384,400 km up.",
    sub: "The air thins, the sky turns black, and your body starts objecting.",
  },
  collectionSlugs: ["space-is-trying-to-kill-you", "cosmic-mysteries"],
  collectionSlug: "space-is-trying-to-kill-you",
  maxDepth: 384400,
  unit: "km",
  curve: 3.2,
  zones: [
    { under: 12, label: "Troposphere" },
    { under: 50, label: "Stratosphere" },
    { under: 100, label: "Mesosphere" },
    { under: 600, label: "Low Earth orbit" },
    { under: 40000, label: "High orbit" },
    { under: Infinity, label: "Cislunar space" },
  ],
  statuses: [
    { under: 3, text: "Ordinary air. You would only notice the wind." },
    { under: 6, text: "Half the oxygen you are used to. Thinking gets slow up here." },
    { under: 9, text: "Death zone. Your body is consuming itself faster than it can recover." },
    { under: 19, text: "Above the Armstrong limit your blood would boil at body temperature." },
    { under: 100, text: "There is not enough air left for a wing to bite into." },
    { under: 2000, text: "Officially space. Unshielded, radiation is now the thing that kills you." },
    { under: 60000, text: "Past the inner Van Allen belt. Astronauts cross this fast, on purpose." },
    { under: Infinity, text: "Deep quiet. A three-day trip, and nobody is coming to get you." },
  ],
  accent: "#a78bfa",
  direction: "up",
  colorStops: [
    { depth: 0, rgb: [14, 20, 42] },
    { depth: 12, rgb: [11, 16, 36] },
    { depth: 50, rgb: [9, 12, 30] },
    { depth: 100, rgb: [7, 9, 24] },
    { depth: 2000, rgb: [4, 5, 16] },
    { depth: 384400, rgb: [2, 2, 8] },
  ],
  outro: "Every human who has ever lived, except two dozen of them, has stayed below the first 400 km.",
  shareLine: "I made it {v} km up before space got unfriendly. See how far you get:",
  nodes: [
    { depthMeters: 0, type: "marker", label: "Ground level", subtext: "One atmosphere of air on your shoulders. You never notice it." },
    { depthMeters: 1.9, type: "discovery", slug: "mount-washington-the-pinnacle-of-pneuma" },
    { depthMeters: 5.5, type: "marker", label: "Half the atmosphere is below you", subtext: "Everything above this point is thinner than the air you have ever breathed." },
    { depthMeters: 8.8, type: "marker", label: "Summit of Everest", subtext: "The death zone. Bodies fail here even with training and bottled oxygen." },
    { depthMeters: 12, type: "discovery", slug: "a-lightning-bolt-is-5-hotter-than-the-sun" },
    { depthMeters: 19, type: "marker", label: "The Armstrong limit", subtext: "Pressure drops so low that the water in your body boils at 37\u00b0C." },
    { depthMeters: 100, type: "marker", label: "The K\u00e1rm\u00e1n line", subtext: "The official start of space. No air left to fly through \u2014 only orbit works." },
    { depthMeters: 120, type: "discovery", slug: "tardigrades-survived-the-vacuum-of-space" },
    { depthMeters: 170, type: "discovery", slug: "sound-waves-vacuum-medium" },
    { depthMeters: 260, type: "discovery", slug: "animal-that-can-survive-space" },
    { depthMeters: 408, type: "marker", label: "The Space Station", subtext: "Falling around Earth at 28,000 km/h and missing the ground every time." },
    { depthMeters: 430, type: "discovery", slug: "beaver-dams-visible-from-space" },
    { depthMeters: 900, type: "discovery", slug: "raspberry-rum-gas-cloud-space" },
    { depthMeters: 2000, type: "discovery", slug: "there-s-a-nebula-shaped-exactly-like-a-human-eye" },
    { depthMeters: 4000, type: "discovery", slug: "sun-true-white-color" },
    { depthMeters: 7000, type: "discovery", slug: "gravity-acts-like-a-magnifying-glass" },
    { depthMeters: 12000, type: "discovery", slug: "the-wow-signal-1977" },
    { depthMeters: 20000, type: "discovery", slug: "neutron-star-weight-teaspoon" },
    { depthMeters: 35786, type: "marker", label: "Geostationary orbit", subtext: "Sit here and you hover over one spot on Earth forever. Every TV satellite lives at this ring." },
    { depthMeters: 50000, type: "discovery", slug: "why-does-anything-exist-matter-antimatter-asymmetry" },
    { depthMeters: 70000, type: "discovery", slug: "black-holes-might-lead-somewhere" },
    { depthMeters: 100000, type: "discovery", slug: "venus-rotates-slower-than-it-orbits" },
    { depthMeters: 130000, type: "discovery", slug: "venus-hottest-planet-solar-system" },
    { depthMeters: 165000, type: "discovery", slug: "diamond-rain-on-gas-giants" },
    { depthMeters: 200000, type: "discovery", slug: "jupiter-moon-io-volcanic-activity" },
    { depthMeters: 240000, type: "discovery", slug: "mars-has-dust-storms-that-cover-the-whole-planet" },
    { depthMeters: 285000, type: "discovery", slug: "pluto-russia-surface-area-comparison" },
    { depthMeters: 330000, type: "discovery", slug: "moon-dust-glass-needles" },
    { depthMeters: 370000, type: "discovery", slug: "moon-escaping-earth-orbit" },
    { depthMeters: 384400, type: "marker", label: "The Moon", subtext: "The furthest any human has been from home. Twelve people have stood on it." },
  ],
};

/** Deep Time - today back to the formation of Earth, in years ago. */
export const DEEP_TIME: DescentConfig = {
  key: "deep-time",
  title: "Deep Time",
  intro:
    "Scroll backwards. From this morning to the day Earth formed, 4.6 billion years ago. Almost none of it had anything resembling you in it.",
  card: {
    eyebrow: "Deep Time · Interactive",
    line: "Scroll back 4.6 billion years, one era at a time.",
    sub: "Humans occupy the last blink. Everything before it is stranger.",
  },
  collectionSlugs: ["ancient-earth-files"],
  collectionSlug: "ancient-earth-files",
  maxDepth: 4_600_000_000,
  unit: "yrs ago",
  curve: 3.4,
  format: bigYears,
  zones: [
    { under: 12_000, label: "Recorded history" },
    { under: 2_600_000, label: "The Ice Ages" },
    { under: 66_000_000, label: "Age of mammals" },
    { under: 252_000_000, label: "Age of dinosaurs" },
    { under: 541_000_000, label: "Before the dinosaurs" },
    { under: 2_500_000_000, label: "Only microbes" },
    { under: Infinity, label: "A molten planet" },
  ],
  statuses: [
    { under: 5_000, text: "People are writing things down. You could probably find breakfast." },
    { under: 300_000, text: "Your own species does not exist yet." },
    { under: 3_000_000, text: "Ice sheets are grinding over half the northern world." },
    { under: 66_000_000, text: "Mammals are small, nervous and mostly nocturnal." },
    { under: 252_000_000, text: "The air is fine, but everything big here can eat you." },
    { under: 541_000_000, text: "Oxygen is unreliable. There is nothing on land at all." },
    { under: 2_500_000_000, text: "The atmosphere would kill you in one breath. No oxygen in it." },
    { under: Infinity, text: "Not a planet yet. Molten rock, constant impacts, no ground to stand on." },
  ],
  accent: "#ff8a3d",
  colorStops: [
    { depth: 0, rgb: [26, 22, 20] },
    { depth: 12_000, rgb: [24, 19, 17] },
    { depth: 2_600_000, rgb: [24, 16, 13] },
    { depth: 66_000_000, rgb: [26, 13, 9] },
    { depth: 541_000_000, rgb: [24, 9, 6] },
    { depth: 2_500_000_000, rgb: [20, 6, 4] },
    { depth: 4_600_000_000, rgb: [12, 3, 2] },
  ],
  outro: "If Earth's history were one day long, humans show up in the last two seconds of it.",
  shareLine: "I scrolled back {v} years into Earth's past. See how far you get:",
  nodes: [
    { depthMeters: 0, type: "marker", label: "Today", subtext: "The thinnest possible slice of the planet's story. Scroll back." },
    { depthMeters: 3_000, type: "discovery", slug: "honey-the-immortal-food" },
    { depthMeters: 11_700, type: "marker", label: "The last ice age ends", subtext: "Sea levels rise 120 metres. Whole coastlines and their people vanish underwater." },
    { depthMeters: 48_500, type: "discovery", slug: "ancient-giant-viruses-thawing-siberian-permafrost" },
    { depthMeters: 120_000, type: "discovery", slug: "humans-and-lions-ancient-coexistence" },
    { depthMeters: 1_200_000, type: "discovery", slug: "afro-hair-evolutionary-shield" },
    { depthMeters: 2_600_000, type: "marker", label: "The Ice Ages begin", subtext: "Glacial cycles start locking and unlocking the planet every hundred thousand years." },
    { depthMeters: 3_600_000, type: "discovery", slug: "why-the-megalodon-really-went-extinct" },
    { depthMeters: 14_000_000, type: "discovery", slug: "antarctica-driest-place-on-earth" },
    { depthMeters: 60_000_000, type: "discovery", slug: "why-ancient-trees-didnt-rot" },
    { depthMeters: 66_000_000, type: "marker", label: "The asteroid", subtext: "Ten kilometres wide, into shallow sea. Three quarters of all species gone." },
    { depthMeters: 68_000_000, type: "discovery", slug: "t-rex-had-feathers-and-we-have-proof" },
    { depthMeters: 100_000_000, type: "discovery", slug: "how-museums-reconstruct-giant-skeletons" },
    { depthMeters: 200_000_000, type: "discovery", slug: "when-is-a-dinosaur-not-a-dinosaur" },
    { depthMeters: 234_000_000, type: "discovery", slug: "earth-once-hit-by-million-year-rain" },
    { depthMeters: 252_000_000, type: "marker", label: "The Great Dying", subtext: "The worst extinction ever recorded. Around 90% of marine species stop existing." },
    { depthMeters: 280_000_000, type: "discovery", slug: "cycads-living-fossil-cones" },
    { depthMeters: 310_000_000, type: "discovery", slug: "ancient-giant-insects-oxygen-rich-air" },
    { depthMeters: 541_000_000, type: "marker", label: "The Cambrian explosion", subtext: "Almost every animal body plan alive today appears in a geological instant." },
    { depthMeters: 2_400_000_000, type: "marker", label: "The Great Oxidation", subtext: "Microbes flood the air with oxygen and poison most of the life that made it." },
    { depthMeters: 3_000_000_000, type: "discovery", slug: "the-ancient-land-that-never-went-underwater" },
    { depthMeters: 3_500_000_000, type: "marker", label: "The oldest fossils", subtext: "Mats of bacteria in shallow water. This is life's earliest surviving evidence." },
    { depthMeters: 4_600_000_000, type: "marker", label: "Earth forms", subtext: "A ball of molten rock being hit by other rocks. No ocean, no air, no ground." },
  ],
};

export const JOURNEYS: DescentConfig[] = [OCEAN_DESCENT, SPACE_ASCENT, DEEP_TIME];

export function journeyByKey(key: string): DescentConfig | undefined {
  return JOURNEYS.find((j) => j.key === key);
}
